import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "./ui/form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Textarea } from "./ui/textarea";
import {
    CheckCircle2,
    Building2,
    User,
    Mail,
    Phone,
    ChevronLeft,
    ChevronRight,
    Loader2,
    AlertCircle,
    Download,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

// Configure your webhook URL and credentials here
const WEBHOOK_URL = import.meta.env.VITE_WEBHOOK_URL || "https://automacao-vertice-webhook.eleevait.com.br/webhook/captacao_leads";
const WEBHOOK_USERNAME = import.meta.env.VITE_WEBHOOK_USERNAME || "";
const WEBHOOK_PASSWORD = import.meta.env.VITE_WEBHOOK_PASSWORD || "";

const downloadFormSchema = z.object({
    fullName: z.string().min(3, "Nome deve ter no mínimo 3 caracteres").max(100, "Nome muito longo"),
    email: z.string().email("Email inválido").max(255, "Email muito longo"),
    phone: z.string().min(10, "Telefone é obrigatório").max(20, "Telefone inválido"),
    companyName: z.string().min(2, "Nome da empresa obrigatório").max(100, "Nome muito longo"),
    companySize: z.enum(["1-50", "51-200", "201-1000", "1000+"], {
        errorMap: () => ({ message: "Selecione o tamanho da empresa" })
    }),
    role: z.string().min(2, "Cargo obrigatório").max(100, "Cargo muito longo"),
    sector: z.string().min(1, "Selecione um setor"),
    interests: z.array(z.string()).optional(),
    message: z.string().max(500, "Mensagem muito longa").optional(),
});

type DownloadFormData = z.infer<typeof downloadFormSchema>;

interface MaterialDownloadFormProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    resourceTitle: string;
}

const interestOptions = [
    { id: "tickets", label: "Gestão de Chamados Unificada" },
    { id: "automation", label: "Automações No-Code" },
    { id: "via", label: "VIA - Inteligência Artificial" },
    { id: "analytics", label: "Analytics e Relatórios" },
    { id: "integrations", label: "Integrações com Sistemas Existentes" },
];

const sectors = [
    "Tecnologia/SaaS",
    "Educação",
    "Saúde",
    "Manufatura",
    "Serviços Financeiros",
    "Varejo/E-commerce",
    "Outro",
];

const MaterialDownloadForm = ({ open, onOpenChange, resourceTitle }: MaterialDownloadFormProps) => {
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const { executeRecaptcha } = useGoogleReCaptcha();

    const form = useForm<DownloadFormData>({
        resolver: zodResolver(downloadFormSchema),
        defaultValues: {
            fullName: "",
            email: "",
            phone: "",
            companyName: "",
            companySize: undefined,
            role: "",
            sector: "",
            interests: [],
            message: "",
        },
    });

    const handleClose = () => {
        onOpenChange(false);
        setTimeout(() => {
            setStep(1);
            setSubmitSuccess(false);
            setSubmitError(false);
            form.reset();
        }, 300);
    };

    const onSubmit = async (data: DownloadFormData) => {
        setIsSubmitting(true);
        setSubmitError(false);

        // Generate reCAPTCHA token
        if (!executeRecaptcha) {
            console.error("reCAPTCHA not yet available");
            toast({
                title: "Erro de Segurança",
                description: "Sistema de proteção ainda carregando. Aguarde um momento.",
                variant: "destructive",
            });
            setIsSubmitting(false);
            return;
        }

        let recaptchaToken: string;
        try {
            recaptchaToken = await executeRecaptcha("material_download_submit");
        } catch (error) {
            console.error("reCAPTCHA error:", error);
            toast({
                title: "Erro de Segurança",
                description: "Não foi possível validar a segurança do formulário.",
                variant: "destructive",
            });
            setIsSubmitting(false);
            return;
        }

        const payload = {
            timestamp: new Date().toISOString(),
            source: "aroan_landing_page",
            type: "material_download",
            resource: resourceTitle,
            recaptchaToken: recaptchaToken,
            contact: {
                fullName: data.fullName,
                email: data.email,
                phone: data.phone,
            },
            company: {
                name: data.companyName,
                size: data.companySize,
                sector: data.sector,
                role: data.role,
            },
            interests: data.interests || [],
            message: data.message || null,
        };

        try {
            // Create Basic Auth header
            const credentials = btoa(`${WEBHOOK_USERNAME}:${WEBHOOK_PASSWORD}`);

            const response = await fetch(WEBHOOK_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Basic ${credentials}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setSubmitSuccess(true);
            toast({
                title: "Material Enviado!",
                description: "Verifique seu email para acessar o conteúdo.",
            });
        } catch (error) {
            console.error("Webhook submission error:", error);
            setSubmitError(true);
            toast({
                title: "Erro ao solicitar material",
                description: "Por favor, tente novamente ou entre em contato diretamente.",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const validateStep = async () => {
        let fieldsToValidate: (keyof DownloadFormData)[] = [];

        if (step === 1) {
            fieldsToValidate = ["fullName", "email", "phone", "companyName", "companySize", "role", "sector"];
        }

        if (fieldsToValidate.length > 0) {
            const isValid = await form.trigger(fieldsToValidate);
            if (!isValid) return;
        }

        setStep(step + 1);
    };

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center gap-2 mb-6">
            {[1, 2].map((i) => (
                <div
                    key={i}
                    className={cn(
                        "h-2 rounded-full transition-all duration-300",
                        i === step ? "w-8 bg-primary" : "w-2 bg-muted",
                        i < step && "bg-primary/50"
                    )}
                />
            ))}
        </div>
    );

    const renderStep1 = () => (
        <div className="space-y-4">
            <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Nome Completo
                        </FormLabel>
                        <FormControl>
                            <Input placeholder="João Silva" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="grid sm:grid-cols-2 gap-4">
                <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                Email Corporativo
                            </FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="joao@empresa.com" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="flex items-center gap-2">
                                <Phone className="w-4 h-4" />
                                Telefone
                            </FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+55 11 99999-9999" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Nome da Empresa
                        </FormLabel>
                        <FormControl>
                            <Input placeholder="Empresa LTDA" {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <div className="grid sm:grid-cols-2 gap-4">
                <FormField
                    control={form.control}
                    name="companySize"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Tamanho da Empresa</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="1-50">1-50 funcionários</SelectItem>
                                    <SelectItem value="51-200">51-200 funcionários</SelectItem>
                                    <SelectItem value="201-1000">201-1000 funcionários</SelectItem>
                                    <SelectItem value="1000+">1000+ funcionários</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <FormField
                    control={form.control}
                    name="sector"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Setor de Atuação</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    {sectors.map((sector) => (
                                        <SelectItem key={sector} value={sector}>
                                            {sector}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Cargo/Função</FormLabel>
                        <FormControl>
                            <Input placeholder="CTO, Gerente de TI, etc." {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <FormField
                control={form.control}
                name="interests"
                render={() => (
                    <FormItem>
                        <FormLabel className="text-base">Áreas de Interesse (Opcional)</FormLabel>
                        <p className="text-sm text-muted-foreground mb-3">
                            Selecione os tópicos que você gostaria de saber mais
                        </p>
                        <div className="space-y-3">
                            {interestOptions.map((interest) => (
                                <FormField
                                    key={interest.id}
                                    control={form.control}
                                    name="interests"
                                    render={({ field }) => (
                                        <FormItem className="flex items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value?.includes(interest.label)}
                                                    onCheckedChange={(checked) => {
                                                        return checked
                                                            ? field.onChange([...field.value, interest.label])
                                                            : field.onChange(
                                                                field.value?.filter((value) => value !== interest.label)
                                                            );
                                                    }}
                                                />
                                            </FormControl>
                                            <FormLabel className="font-normal cursor-pointer">
                                                {interest.label}
                                            </FormLabel>
                                        </FormItem>
                                    )}
                                />
                            ))}
                        </div>
                        <FormMessage />
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>Mensagem/Observações (opcional)</FormLabel>
                        <FormControl>
                            <Textarea
                                placeholder="Alguma dúvida específica?"
                                className="resize-none min-h-[100px]"
                                {...field}
                            />
                        </FormControl>
                        <div className="flex items-center justify-between">
                            <FormMessage />
                            <p className="text-xs text-muted-foreground">
                                {field.value?.length || 0}/500
                            </p>
                        </div>
                    </FormItem>
                )}
            />
        </div>
    );

    const renderSuccess = () => (
        <div className="text-center py-8 space-y-6 animate-fade-in">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-primary animate-scale-in" />
            </div>
            <div>
                <h3 className="text-2xl font-bold mb-2">
                    Material Enviado com Sucesso!
                </h3>
                <p className="text-muted-foreground">
                    O link para download de <strong>{resourceTitle}</strong> foi enviado para {form.getValues("email")}.
                </p>
            </div>
            <p className="text-sm text-muted-foreground">
                Verifique sua caixa de entrada (e spam) nos próximos minutos.
            </p>
            <Button onClick={handleClose} size="lg" className="w-full">
                Fechar
            </Button>
        </div>
    );

    const renderError = () => (
        <div className="text-center py-8 space-y-6">
            <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-destructive" />
            </div>
            <div>
                <h3 className="text-2xl font-bold mb-2">Erro ao Solicitar Material</h3>
                <p className="text-muted-foreground">
                    Não foi possível processar sua solicitação. Por favor, tente novamente ou
                    entre em contato diretamente.
                </p>
            </div>
            <div className="space-y-2">
                <p className="text-sm">
                    <strong>Email alternativo:</strong>{" "}
                    <a href="mailto:contato@aroan.com" className="text-primary hover:underline">
                        contato@aroan.com
                    </a>
                </p>
            </div>
            <div className="flex gap-3">
                <Button
                    onClick={() => {
                        setSubmitError(false);
                        setStep(2);
                    }}
                    variant="outline"
                    className="flex-1"
                >
                    Tentar Novamente
                </Button>
                <Button onClick={handleClose} variant="ghost" className="flex-1">
                    Fechar
                </Button>
            </div>
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                {submitSuccess ? (
                    renderSuccess()
                ) : submitError ? (
                    renderError()
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="text-2xl flex items-center gap-2">
                                <Download className="w-6 h-6 text-primary" />
                                Baixar Material
                            </DialogTitle>
                            <p className="text-muted-foreground text-sm">
                                Preencha seus dados para receber <strong>{resourceTitle}</strong> gratuitamente.
                            </p>
                        </DialogHeader>

                        {renderStepIndicator()}

                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                {step === 1 && renderStep1()}
                                {step === 2 && renderStep2()}
                            </form>
                        </Form>

                        <div className="flex gap-3 pt-4 border-t">
                            {step > 1 && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setStep(step - 1)}
                                    disabled={isSubmitting}
                                    className="flex-1"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-2" />
                                    Voltar
                                </Button>
                            )}

                            {step < 2 ? (
                                <Button
                                    type="button"
                                    onClick={validateStep}
                                    className="flex-1"
                                >
                                    Próximo
                                    <ChevronRight className="w-4 h-4 ml-2" />
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    onClick={form.handleSubmit(onSubmit)}
                                    disabled={isSubmitting}
                                    className="flex-1"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        "Receber Material"
                                    )}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MaterialDownloadForm;
